import { fabric } from 'fabric';
import { rotatePoints, mirrorPoints, transformToAbsolute } from './Utils';
import { getControlHandler, getDefaultTransform, transformTypes } from './Transform/index';
import { getSampleWarpShape } from './Transform/WarpSamples.js';

function transformPoints(points, target) {
    const matrix = target.calcTransformMatrix();

    return points.map((point) => {
        const transformPoint = new fabric.Point(
            target.width * point.x - target.width / 2,
            target.height * point.y - target.height / 2
        );

        return fabric.util.transformPoint(transformPoint, matrix);
    });
}

function rotatePoint(point, angle) {
    const radians = fabric.util.degreesToRadians(angle);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return {
        x: cos * point.x - sin * point.y,
        y: sin * point.x + cos * point.y
    };
}

const controlTransform = {
    toggleEdit(status) {
        const transform = this.transformOptions;
        if (status) {
            this.setEditMode(this.transformOptions, transform);
        } else if (this.inPathEditMode) {
            this.setEditMode(undefined);
        } else {
            this.setTransform(transform, {
                justEnabledFreeLineEdit: true
            });
        }
    },

    translateToControlPoint() {
        if (!this.transformOptions?.type || !this._controlVector || !this.points) {
            return;
        }

        const flippedVector = {
            x: (this.flipX ? -1 : 1) * this._controlVector.x,
            y: (this.flipY ? -1 : 1) * this._controlVector.y
        };

        const rotatedVector = rotatePoint(flippedVector, this.angle);
        const firstPoint = this.points[0];

        this.top = firstPoint.y - rotatedVector.y;
        this.left = firstPoint.x - rotatedVector.x;

        this.updateLastPos();
    },

    getTextCenterPoint() {
        if (!this.singleline && void 0 !== this.width && ['left', 'right'].includes(this.textAlignment)) {
            const dimensions = this.pathText._calcDimensions();
            const xPosition =
                'left' === this.textAlignment
                    ? dimensions.width / this.width / 2
                    : 1 - dimensions.width / this.width / 2;
            return transformPoints(
                [
                    {
                        x: xPosition,
                        y: 0.5
                    }
                ],
                this.pathText
            )[0];
        }
        return this.getAbsoluteCenterPoint();
    },

    adjustToCenter(targetPoint) {
        const currentCenter = this.getTextCenterPoint();
        const offset = {
            x: targetPoint.x - currentCenter.x,
            y: targetPoint.y - currentCenter.y
        };

        this.set({
            top: this.top + offset.y,
            left: this.left + offset.x
        });

        this.set('dirty', true);

        this.handleOnMove();
    },

    createWarpTransform(typeSample = transformTypes.warpForm) {
        const warpform = getSampleWarpShape(typeSample);
        const points = transformToAbsolute(warpform, this);
        this.setPoints(points);
    },

    createResetTransform() {
        const resetform = getDefaultTransform(transformTypes.resetFrom);
        const points = transformToAbsolute(resetform, this);
        this.setPoints(points);
    },

    setEditMode(edit, options) {
        options = options || false;
        const wasInEditMode = this.inPathEditMode;
        this.inPathEditMode = !!edit;
        if (edit && this.inPathEditMode) {
            const type = this.transformOptions?.type || undefined;
            const isOptionsValid = options || type !== edit.type;
            if (!this.points || isOptionsValid) {
                const textCenterPoint = this.getTextCenterPoint();
                this.set('singleline', true);
                this.updatePathText({
                    callback: () => {
                        this.set('transformOptions', {
                            ...edit
                        });
                        if (edit.type === transformTypes.warpForm) {
                            this.createWarpTransform(options?.typeSample);
                        } else if (edit.type === transformTypes.resetFrom) {
                            this.createResetTransform();
                        }
                        this.updateTransformControls();
                        this.updatePathText({
                            callback: () => {
                                this.adjustToCenter(textCenterPoint);
                                if (options) {
                                    this.adjustableWidth = undefined;
                                }
                                this._fireModified();
                            }
                        });
                    }
                });
            } else {
                this.set('transformOptions', {
                    ...edit,
                    points: this.points
                });
                this.updateTransformControls();
                this.updatePathText({
                    callback: () => {
                        this._fireModified();
                    }
                });
            }
        } else if (wasInEditMode) {
            this._fireModified();
        }
        this.updateTransformControls();
    },

    updateTransformControls() {
        const type = this.transformOptions.type;

        if (this.inPathEditMode && type) {
            this.cornerStyle = 'circle';
            this.hasBorders = false;

            const handler = getControlHandler(type);
            const baseControls = {
                l: handler.line(fabric)
            };
            this.controls = this.points.reduce((controls, point, index) => {
                controls[`p${index}`] = handler.point(fabric, index, this);
                return controls;
            }, baseControls);
            return;
        }

        this.hasBorders = true;
        this.cornerStyle = 'square';
        this.resetControls();
    },

    resetControls() {
        this.controls = {
            ...fabric.Object.prototype.controls
        };
    },

    getAbsoluteTopLeft() {
        return {
            top: this.top,
            left: this.left
        };
    },

    updateLastPos() {
        this.lastPos = this.getAbsoluteTopLeft();
    },

    updateLastFlip() {
        this.lastFlip = {
            x: this.flipX,
            y: this.flipY
        };
    },

    handleRotation(object, useTopLeft, customCenter) {
        if (this.points) {
            // Determine rotation center point
            const centerPoint =
                customCenter ?? (useTopLeft ? object.getAbsoluteTopLeftPoint() : object.getAbsoluteCenterPoint());
            // Rotate points around the center
            const rotatedPoints = rotatePoints(this.points, centerPoint, object.lastAngle, object.angle);

            this.setPoints(rotatedPoints);
        }

        // Update position and angle tracking
        this.updateLastPos?.();
        this.updateLastAngle?.();
    },

    updateLastAngle() {
        this.lastAngle = this.angle;
    },

    updateLastScale() {
        const { scale } = this.getTextScale();
        this.lastScale = scale;
    },

    setPoints(newPoints) {
        this.points = newPoints;
        this.transformOptions = {
            ...this.transformOptions,
            points: newPoints
        };
        if (this?.editMode) {
            this.setEditedProp?.('transformOptions');
        }
    },

    setLastPoints() {
        this.lastPoints = [...this.points];
    },

    getAbsoluteCenterPoint() {
        const transformMatrix = this.calcTransformMatrix();
        return fabric.util.transformPoint(
            {
                x: 0,
                y: 0
            },
            transformMatrix
        );
    },

    handleOnMove() {
        const topLeftCoordinates = this.getAbsoluteTopLeft();
        if (this.lastPos && this.points) {
            const horizontalOffset = topLeftCoordinates.left - this.lastPos.left;
            const verticalOffset = topLeftCoordinates.top - this.lastPos.top;
            const offsetPoints = this.points.map((e) => ({
                x: e.x + horizontalOffset,
                y: e.y + verticalOffset
            }));
            this.setPoints(offsetPoints);
        }
        this.lastPos = topLeftCoordinates;
        this.updateFakeDashArray();
    },

    handleOnScale() {
        const { scale: newScale } = this.getTextScale();
        if (this.points) {
            this._pathTextWasChangedAfterUpdate = true;
            const lastPosition = this.lastPos;
            const normalizedScale = (1 / this.lastScale) * newScale;
            const calculateScaledPosition = (e) => ({
                x: lastPosition.left + (e.x - lastPosition.left) * normalizedScale,
                y: lastPosition.top + (e.y - lastPosition.top) * normalizedScale
            });
            this.setPoints(this.points.map(calculateScaledPosition));
            this.handleOnMove();
        }
        this.lastScale = newScale;
    },

    handleScale() {
        const { scale: newScale } = this.getTextScale();

        if (this.points) {
            this._pathTextWasChangedAfterUpdate = true;
            const origin = this.lastPos;
            const scaleFactor = (1 / this.lastScale) * newScale;

            const scalePoint = (point) => ({
                x: origin.left + (point.x - origin.left) * scaleFactor,
                y: origin.top + (point.y - origin.top) * scaleFactor
            });

            this.setPoints(this.points.map(scalePoint));
            this.handleOnMove();
        }

        this.lastScale = newScale;
    },

    handleFlip() {
        const currentFlip = { x: this.flipX, y: this.flipY };
        const xFlipped = currentFlip.x !== this.lastFlip.x;
        const yFlipped = currentFlip.y !== this.lastFlip.y;

        if (!this.points || (!xFlipped && !yFlipped)) {
            this.lastFlip = currentFlip;
            return;
        }

        this._pathTextWasChangedAfterUpdate = true;
        const { tl, tr, br, bl } = this.getAbsoluteCoords();
        let updatedPoints = this.points;

        if (xFlipped) {
            const bottomMid = {
                x: bl.x + (br.x - bl.x) / 2,
                y: bl.y + (br.y - bl.y) / 2
            };
            const topMid = {
                x: tl.x + (tr.x - tl.x) / 2,
                y: tl.y + (tr.y - tl.y) / 2
            };
            updatedPoints = mirrorPoints(updatedPoints, bottomMid, topMid);
        }

        if (yFlipped) {
            const leftMid = {
                x: bl.x + (tl.x - bl.x) / 2,
                y: bl.y + (tl.y - bl.y) / 2
            };
            const rightMid = {
                x: br.x + (tr.x - br.x) / 2,
                y: br.y + (tr.y - br.y) / 2
            };
            updatedPoints = mirrorPoints(updatedPoints, leftMid, rightMid);
        }

        this.setPoints(updatedPoints);
        this.lastFlip = currentFlip;
    }
};

fabric.util.object.extend(fabric.TextTransform.prototype, controlTransform);
